import { FormEvent, useState } from 'react'
import classNames from 'classnames'

import type { FormModuleComponent } from 'services/content/components/types/FormModule'
import Component from './Component'
import ReCaptcha from 'ui-component-library/components/00-Primitives/ReCaptcha'
import SubmitButton from 'ui-component-library/components/00-Primitives/Inputs/Submit'
import Loader from 'content-components/_Primatives/Loader'
import TextBlock from 'ui-component-library/components/00-Primitives/TextBlock'
import useFormFields from './useFormFields'
import style from './style.scss'

const IDLE_STATE = 'idle'
const SUBMITTING_STATE = 'submitting'
const SUCCESS_STATE = 'success'
const RECAPTCHA_ERROR_STATE = 'recaptcha-error'
const INVALID_STATE = 'invalid'
const ERROR_STATE = 'error'

type FormModuleState =
  | typeof IDLE_STATE
  | typeof SUBMITTING_STATE
  | typeof SUCCESS_STATE
  | typeof RECAPTCHA_ERROR_STATE
  | typeof INVALID_STATE
  | typeof ERROR_STATE

type Props = {
  components: FormModuleComponent[]
  submitButtonText: string
  onSubmit: (values: Record<string, string>) => void
  recaptcha?: {
    apiKey: string
    errorText: string
  }
  successText: string
  errorText: string
  invalidText: string
  styleOverrides?: {
    wrapper: string
    submitButton: string
    component: string
    successText: string
  }
}

function FormModule({
  components,
  submitButtonText,
  onSubmit,
  recaptcha,
  successText,
  errorText,
  invalidText,
  styleOverrides,
}: Props) {
  const [state, setState] = useState<FormModuleState>(IDLE_STATE)
  const { values, onChange, isValid } = useFormFields(components)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setState(SUBMITTING_STATE)

    if (isValid) {
      try {
        await onSubmit(values)
        setState(SUCCESS_STATE)
      } catch (error) {
        setState(ERROR_STATE)
      }
    } else {
      setState(INVALID_STATE)
    }
  }

  if (state === SUCCESS_STATE) {
    return (
      <TextBlock
        className={classNames(
          style.wrapper,
          styleOverrides && styleOverrides.successText,
        )}
      >
        {successText}
      </TextBlock>
    )
  }

  return (
    <form
      className={classNames(
        style.wrapper,
        styleOverrides && styleOverrides.wrapper,
      )}
      onSubmit={handleSubmit}
    >
      {components.map((component, index) => (
        <Component
          key={`${component.name}_${index}`}
          component={component}
          values={values}
          onChange={onChange}
          className={styleOverrides && styleOverrides.component}
        />
      ))}

      {recaptcha && (
        <ReCaptcha
          className={style.recaptcha}
          apiKey={recaptcha.apiKey}
          onChange={(tokenValue) => onChange('recaptcha', tokenValue)}
        />
      )}

      {state === RECAPTCHA_ERROR_STATE && <div>{recaptcha?.errorText}</div>}

      {state === SUBMITTING_STATE ? (
        <Loader small />
      ) : (
        <SubmitButton
          className={classNames(
            style.submitButton,
            styleOverrides && styleOverrides.submitButton,
          )}
          submitText={submitButtonText}
        />
      )}

      {state === ERROR_STATE && <div>{errorText}</div>}
      {state === INVALID_STATE && <div>{invalidText}</div>}
    </form>
  )
}

export default FormModule
